/** Admin Settings → API: automatic database backups (all brands) to OneDrive / Google Drive + "Back up now". */
import { useEffect, useState } from "react";
import { CloudUpload, DatabaseBackup, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import CloudRestore from "./CloudRestore";
import RetentionAndAlerts from "./RetentionAndAlerts";

const NAMES = { onedrive: "OneDrive", google: "Google Drive" };

export default function CloudBackupSettings() {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/cloud-backup").then(({ data }) => setS(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!s?.running) return undefined; const t = setInterval(load, 4000); return () => clearInterval(t); }, [s?.running]);
  if (!s) return null;

  const save = async (patch, msg) => {
    try { const { data } = await api.put("/admin/cloud-backup", { enabled: s.enabled, providers: s.providers, time: s.time, ...patch }); setS(data); if (msg) toast.success(msg); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };
  const toggleProvider = (p) => save({ providers: s.providers.includes(p) ? s.providers.filter((x) => x !== p) : [...s.providers, p] });
  const runNow = async (p) => {
    setBusy(p);
    try { await api.post("/admin/cloud-backup/run", { providers: [p] }); toast.success(`Backing up Elite, Steeltooth and Jasper to ${NAMES[p]}…`); setTimeout(load, 1500); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start backup"); }
    finally { setBusy(""); }
  };
  const linked = s.linked_here || [];
  const lr = s.last_result;

  return (
    <div className="space-y-3" data-testid="cloud-backup-settings">
      <div className="mt-6 flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
        <DatabaseBackup className="h-4 w-4 text-emerald-400" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-white">Automatic cloud backup (all brands)</p>
          <p className="text-xs text-white/40">Every night, a full zip of the Elite, Steeltooth and Jasper databases (+ uploaded files) lands in "<em>Company</em> Portal / Database backups" on the drive(s) you pick.</p>
        </div>
        <button onClick={() => save({ enabled: !s.enabled }, s.enabled ? "Auto backup OFF" : `Auto backup ON — nightly at ${s.time}`)} aria-pressed={!!s.enabled} disabled={!s.enabled && !s.providers.length}
          className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1 text-xs font-bold ${s.enabled ? "bg-emerald-600 text-white" : "bg-white/10 text-white/60"} disabled:opacity-40`} title={!s.providers.length ? "Pick a drive first" : ""} data-testid="cloud-backup-toggle">{s.enabled ? "ON" : "OFF"}</button>
      </div>
      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-xs text-white/70">
        <span className="font-semibold text-white/80">Back up to</span>
        {["onedrive", "google"].map((p) => (
          <label key={p} className={`inline-flex items-center gap-1.5 ${linked.includes(p) ? "" : "opacity-50"}`} title={linked.includes(p) ? "" : "Link it first under Files → " + NAMES[p]}>
            <input type="checkbox" checked={s.providers.includes(p)} disabled={!linked.includes(p)} onChange={() => toggleProvider(p)} className="accent-[#1a3fd1]" data-testid={`cloud-backup-provider-${p}`} /> {NAMES[p]}{linked.includes(p) ? "" : " (not linked)"}
          </label>
        ))}
        <label className="inline-flex items-center gap-1.5">at <input type="time" value={s.time} onChange={(e) => save({ time: e.target.value })} className="rounded-md border border-white/15 bg-white/5 px-2 py-1 text-white" data-testid="cloud-backup-time" /></label>
        <span className="ml-auto flex gap-2">
          {["onedrive", "google"].map((p) => linked.includes(p) && (
            <button key={p} onClick={() => runNow(p)} disabled={!!busy || s.running} className="inline-flex items-center gap-1.5 rounded-md bg-[#1a3fd1] px-2.5 py-1.5 font-semibold text-white disabled:opacity-50" data-testid={`cloud-backup-now-${p}`}>
              {busy === p || s.running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CloudUpload className="h-3.5 w-3.5" />} Back up now → {NAMES[p]}
            </button>
          ))}
          {!linked.length && <span className="text-white/40">Link OneDrive or Google Drive under Files to enable backups.</span>}
        </span>
      </div>
      {s.running && <p className="text-xs text-amber-300" data-testid="cloud-backup-running"><Loader2 className="mr-1 inline h-3 w-3 animate-spin" /> Backup running — dumping all brands and uploading…</p>}
      {s.stale && !s.running && (
        <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs text-amber-200" data-testid="cloud-backup-stale">
          <AlertCircle className="mr-1 inline h-3.5 w-3.5" /> Auto backup is ON but nothing has completed for 2+ days{s.last_ok_at ? ` (last success ${new Date(s.last_ok_at).toLocaleString()})` : ""}. Check the drive link and run a backup now.
        </p>
      )}
      <RetentionAndAlerts s={s} save={save} onAlertSent={load} />
      {lr && (
        <div className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-xs" data-testid="cloud-backup-last">
          <div className="flex items-center gap-2 font-semibold text-white/80">
            {lr.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <AlertCircle className="h-4 w-4 text-amber-400" />}
            Last backup {new Date(lr.finished).toLocaleString()} · {lr.trigger}
          </div>
          <ul className="mt-1 space-y-0.5 text-white/60">
            {lr.results.map((r, i) => (
              <li key={i} className={r.error ? "text-amber-300" : ""}>
                {r.retention !== undefined ? (
                  <>{NAMES[r.provider]} · retention: kept {r.retention?.kept ?? "?"}, removed {r.retention?.removed?.length ?? 0} old backup(s){r.error ? ` — ${r.error}` : ""}</>
                ) : (
                  <>{r.provider ? `${NAMES[r.provider]} · ` : ""}{r.workgroup || ""}{r.file ? ` · ${r.file} (${(r.size / 1048576).toFixed(1)} MB, ${r.collections} collections, ${r.uploads} files)` : ""}{r.error ? ` — ${r.error}` : ""}</>
                )}
                {r.web_url && <a href={r.web_url} target="_blank" rel="noreferrer" className="ml-1 text-[#8fa4f0] underline">open</a>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {/* Cloud restore is destructive: superadmin only (backend enforces it too). */}
      {user?.role === "superadmin" && <CloudRestore linked={linked} />}
    </div>
  );
}
